export type CoinType = 'bronze' | 'silver' | 'gold' | 'diamond'

export const COIN_TYPES: CoinType[] = ['diamond', 'gold', 'silver', 'bronze']

export interface CoinMap {
  bronze: number
  silver: number
  gold: number
  diamond: number
}

export type CoinRateCents = Record<CoinType, number>

export interface CoinExchangeRow {
  id: number
  user_id: number
  from_coin: CoinType
  to_coin: CoinType
  amount: number
  spent: number
  received: number
  from_cents: number
  to_cents: number
  created_at: string
}

export interface Wallet {
  id: number
  user_id: number
  coins: CoinMap
  dollar: number
  created_at: string
  updated_at: string
}

export interface RewardCode {
  id: number
  code: string
  user_id: number
  game_type: string
  award_type: string
  date: string
  valid_from: string
  valid_to: string
  is_used: boolean
  used_by: number
  used_at: string
  selected_prize_id?: number
  selected_prize?: string
  created_at: string
}

export interface GiftPrize {
  id: number
  title: string
  description: string
  image_url: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface AirdropScore {
  id: number
  user_id: number
  score: number
  date: string
  created_at: string
  updated_at: string
}

export interface AirdropLeaderboardEntry {
  rank: number
  user_id: number
  username: string
  first_name: string
  photo_url: string
  score: number
}

export interface AirdropStatusResponse {
  is_game_active: boolean
  can_click: boolean
  user_score: number
  daily_top_winners: number
  monthly_top_winners: number
  seconds_remaining: number
  useful_seconds_total: number
  cooldown_seconds_remaining: number
  presence_active: boolean
  time_exhausted: boolean
  can_watch_ad: boolean
}

export interface AirdropClickResponse {
  score: number
  can_click: boolean
  is_game_open: boolean
  seconds_remaining?: number
  score_updated?: boolean
  message: string
}

export interface WSHubMessage {
  type: 'score_update' | 'leaderboard_update' | 'airdrop_click_response' | 'error' | 'auth_response'
  request_id?: string
  user_id?: number
  score?: number
  can_click?: boolean
  is_game_open?: boolean
  seconds_remaining?: number
  message?: string
  error?: string
  success?: boolean
  data?: {
    leaderboard?: AirdropLeaderboardEntry[]
  }
}

export interface WSBroadcastScore {
  type: 'score_update'
  user_id: number
  score: number
}

export interface User {
  id: number
  public_id?: string
  telegram_id: number
  username: string
  first_name: string
  last_name: string
  photo_url: string
  referral_code: string
  invited_by: string
  level: number
  referral_count: number
  is_admin: boolean
  last_login: string
  wallet?: Wallet
  messages: AdminMessage[]
  created_at: string
  updated_at: string
}

export interface AdminUserHistoryItem {
  source: string
  kind: 'coin' | 'score' | 'gift' | 'transfer' | 'activity' | string
  title: string
  detail?: string
  coin_type?: string
  amount?: number
  score?: number
  direction?: 'in' | 'out' | string
  at: string
  meta?: Record<string, unknown>
}

export interface AdminUserFootballScoreView {
  league_id: number
  league_name: string
  total_score: number
  daily_score: number
  weekly_score: number
  monthly_score: number
}

export interface AdminUserScoresSummary {
  airdrop_total: number
  airdrop_today: number
  airdrop_days: AirdropScore[]
  football: AdminUserFootballScoreView[]
  horse_league: UserHorseLeagueScore[]
  referral_count: number
}

export interface AdminUserDetail {
  user: User
  wallet: Wallet | null
  wallet_pin?: string
  scores: AdminUserScoresSummary
  history: AdminUserHistoryItem[]
}

export interface AdminMessage {
  id: number
  admin_id: number
  user_id: number
  text: string
  read: boolean
  sent_at: string
  created_at: string
}

export interface GameRecord {
  id: number
  user_id: number
  game_type: string
  guess_number?: string
  score: number
  level: number
  is_winner?: boolean
  reward_claimed?: boolean
  daily_number_id?: number
  played_at: string
  created_at: string
}

export interface GuessNumberStatus {
  has_active_number: boolean
  current_number: string
  is_revealed: boolean
  end_of_day: boolean
  attempts_remaining: number
  user_has_won: boolean
  winners_count: number
}

export interface GuessResult {
  is_game_over: boolean
  attempts_left: number
  message: string
  is_winner: boolean
}

export interface MorseWordView {
  index: number
  morse: string
  solved: boolean
  word?: string
}

export interface MorseStatus {
  has_round: boolean
  is_window_open: boolean
  session_started: boolean
  session_active: boolean
  seconds_remaining: number
  words: MorseWordView[]
  solved_count: number
  total_words: number
  revealed: boolean
}

export interface MorseGuessResult {
  correct: boolean
  word?: string
  message: string
  solved_count: number
  gold_awarded: number
  all_solved: boolean
}

export interface LuckyStageConfig {
  stage: number
  pool_size: number
  pick_count: number
  reward: CoinType
  label: string
  free_attempts: number
  max_ads: number
  ad_bonus_per_watch: number
}

export interface LuckyStageView {
  stage: number
  pool_size: number
  pick_count: number
  reward: CoinType
  label: string
  free_attempts: number
  max_ads: number
  ad_bonus_per_watch: number
  numbers: number[]
}

export interface LuckyNumbersStatus {
  has_round: boolean
  is_window_open: boolean
  session_started: boolean
  session_active: boolean
  seconds_remaining: number
  current_stage: number
  completed: boolean
  attempts_remaining: number
  free_attempts: number
  ad_bonus_attempts: number
  ads_watched: number
  ads_remaining: number
  can_watch_ad: boolean
  demoted?: boolean
  stage?: LuckyStageView
  stages: LuckyStageConfig[]
  session_minutes: number
}

export interface LuckyPickResult {
  won: boolean
  message: string
  reward?: CoinType
  correct_numbers?: number[]
  current_stage: number
  completed: boolean
  attempts_remaining: number
  demoted?: boolean
}


export type SupportTicketStatus = 'open' | 'closed'
export type SupportMessageSender = 'user' | 'admin'

export interface SupportMessage {
  id: number
  ticket_id: number
  sender: SupportMessageSender
  sender_id: number
  text: string
  created_at: string
}

export interface SupportTicket {
  id: number
  user_id: number
  user?: User
  subject: string
  status: SupportTicketStatus
  last_message_at: string
  messages?: SupportMessage[]
  created_at: string
  updated_at: string
}

export interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  language_code?: string
}

export interface TelegramWebApp {
  initData: string
  initDataUnsafe?: {
    user?: TelegramUser
    auth_date?: number
    hash?: string
    query_id?: string
    start_param?: string
  }
  showAlert: (msg: string) => void
  showConfirm: (msg: string) => Promise<boolean>
  close: () => void
  expand: () => void
  ready: () => void
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void
  openTelegramLink?: (url: string) => void
  MainButton: {
    show: () => void
    hide: () => void
    setText: (text: string) => void
    enable: () => void
    disable: () => void
    onClick: (f: () => void) => void
  }
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (f: () => void) => void
  }
  themeParams?: Record<string, string>
  viewportHeight: number
  viewportStableHeight: number
  isExpanded: boolean
  colorScheme: 'light' | 'dark'
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp
    }
  }
}

export type MatchStatus = 'upcoming' | 'live' | 'finished' | 'cancelled'

export interface FootballClub {
  id: number
  name: string
  flag_url: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface FootballMatch {
  id: number
  home_team: string
  away_team: string
  home_team_id?: number | null
  away_team_id?: number | null
  home_club?: FootballClub | null
  away_club?: FootballClub | null
  start_time: string
  mode: FootballGameMode
  status: MatchStatus
  home_score: number | null
  away_score: number | null
  winner: string
  raffle_drawn: boolean
  created_by: number
  created_at: string
  updated_at: string
}

export type PredictionChoice = 'home' | 'away' | 'draw'

export interface MatchPrediction {
  id: number
  match_id: number
  user_id: number
  choice: PredictionChoice
  choices?: PredictionChoice[]
  is_correct: boolean
  points?: number
  created_at: string
}

export type PlayerPosition = 'gk' | 'def' | 'mid' | 'fwd'

export interface FantasyPlayer {
  id: number
  name: string
  position: PlayerPosition
  team: string
  photo_url: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface PlayerGroup {
  id: number
  title: string
  max_cards: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface MatchFantasyPlayer {
  id: number
  name: string
  position: PlayerPosition
  team: string
  photo_url: string
  group_ids: number[]
  group_titles: string[]
}

export interface MatchFantasyPool {
  match_id: number
  groups: PlayerGroup[]
  players: MatchFantasyPlayer[]
}

export interface FantasyTeamPlayer {
  id: number
  fantasy_team_id: number
  player_id: number
  position: PlayerPosition
  slot_number: number
  player?: FantasyPlayer
}

export interface FantasyTeam {
  id: number
  user_id: number
  match_id: number
  name: string
  formation: string
  share_token: string
  total_votes: number
  avg_rating: number
  vote_count: number
  is_submitted: boolean
  submitted_at: string | null
  created_at: string
  updated_at: string
  players?: FantasyTeamPlayer[]
  user?: {
    id: number
    first_name: string
    last_name: string
    username: string
    photo_url: string
  }
}

export interface TeamExploreItem extends FantasyTeam {
  owner_name: string
  home_team: string
  away_team: string
  start_time: string
  voting_open: boolean
}

export interface MatchVotingInfo {
  match_id: number
  start_time: string
  status: 'upcoming' | 'live' | 'finished' | 'cancelled' | string
  voting_open: boolean
}

export interface TeamVote {
  id: number
  fantasy_team_id: number
  user_id: number
  rating: number
  created_at: string
}

export type TransferType = 'user' | 'admin'
export type TransferStatus = 'pending' | 'approved' | 'rejected' | 'completed'

export interface CoinTransfer {
  id: number
  from_user_id: number
  to_user_id: number | null
  transfer_type: TransferType
  coin_type: CoinType
  amount: number
  status: TransferStatus
  card_number: string
  card_holder: string
  wallet_address: string
  admin_note: string
  processed_by: number | null
  processed_at: string | null
  created_at: string
  updated_at: string
  from_user?: {
    id: number
    first_name: string
    last_name: string
    username: string
  }
}

export interface UserPIN {
  has_pin: boolean
}

export interface FantasyTeamCreateDTO {
  name: string
  formation: string
  match_id: number
  players: {
    player_id: number
    position: PlayerPosition
    slot: number
  }[]
}

export interface VoteDTO {
  team_id: number
  rating: number
}

export interface AdminStats {
  total_users: number
  new_users_today: number
  new_users_week: number
  new_users_month: number
  total_coins: {
    bronze: number
    silver: number
    gold: number
    diamond: number
  }
  airdrop_stats: {
    today_total_clicks: number
    today_participants: number
    month_total_clicks: number
    month_participants: number
  }
  guess_number_stats: {
    today_guesses: number
    today_winners: number
    total_winners: number
    total_games_played: number
  }
  football_stats: {
    total_matches: number
    upcoming_matches: number
    live_matches: number
    finished_matches: number
    total_predictions: number
  }
  fantasy_stats: {
    total_players: number
    total_teams: number
    total_votes: number
  }
  transfer_stats: {
    pending_transfers: number
    approved_transfers: number
    rejected_transfers: number
    completed_transfers: number
  }
  reward_code_stats: {
    total_codes: number
    used_codes: number
    active_codes: number
    daily_codes: number
    monthly_codes: number
  }
}

export type FootballGameMode = 'league' | 'big_match'

export interface FootballLeague {
  id: number
  name: string
  mode: FootballGameMode
  description: string
  start_date: string
  end_date: string
  is_active: boolean
  created_by: number
  created_at: string
  updated_at: string
}

export interface FootballLeagueMatch {
  id: number
  league_id: number
  match_id: number
  match_date: string
  order_index: number
  created_at: string
  match?: FootballMatch
}

export interface UserFootballScore {
  id: number
  user_id: number
  league_id: number
  total_score: number
  daily_score: number
  weekly_score: number
  monthly_score: number
  last_updated: string
  created_at: string
  updated_at: string
  user?: {
    id: number
    first_name: string
    last_name: string
    username: string
    photo_url: string
  }
}

export interface DreamTeamPlayer {
  id: number
  dream_team_id: number
  player_id: number
  position: PlayerPosition
  slot_number: number
  player?: FantasyPlayer
}

export interface DreamTeam {
  id: number
  user_id: number
  match_id: number
  league_id: number
  name: string
  formation: string
  total_score: number
  avg_rating: number
  vote_count: number
  is_submitted: boolean
  submitted_at: string | null
  created_at: string
  updated_at: string
  players?: DreamTeamPlayer[]
  user?: {
    id: number
    first_name: string
    last_name: string
    username: string
    photo_url: string
  }
  match?: FootballMatch
}

export interface DreamTeamExploreItem extends DreamTeam {
  owner_name: string
  home_team: string
  away_team: string
  start_time: string
  share_url: string
  voting_open: boolean
}

export interface DreamTeamVote {
  id: number
  dream_team_id: number
  user_id: number
  rating: number
  created_at: string
}

export interface RaffleWinner {
  id: number
  match_id: number
  league_id: number
  user_id: number
  prize_type: string
  prize_amount: number
  draw_type: string
  awarded_at: string
  created_at: string
  user?: {
    id: number
    first_name: string
    last_name: string
    username: string
    photo_url: string
  }
}

export interface LeagueDayGoalsPrediction {
  id: number
  user_id: number
  league_id: number
  match_date: string
  predicted_home_total: number
  predicted_away_total: number
  paid_with: 'bronze' | 'ads' | string
  is_correct?: boolean | null
  awarded_at?: string | null
  created_at: string
}

export interface LeagueDayGoalsStatus {
  eligible: boolean
  match_date?: string
  match_count: number
  fee_bronze: number
  reward_silver: number
  submitted: boolean
  prediction?: LeagueDayGoalsPrediction | null
}

export interface UserLeagueScore {
  league_id: number
  league_name: string
  mode: FootballGameMode
  total_score: number
  daily_score: number
  monthly_score: number
  rank: number
}

export interface MatchPlayerStat {
  id: number
  match_id: number
  player_id: number
  position: PlayerPosition
  goals: number
  assists: number
  yellow_cards: number
  clean_sheet: boolean
  played_full_time: boolean
}

export interface DreamTeamCreateDTO {
  name: string
  formation: string
  match_id: number
  players: {
    player_id: number
    position: PlayerPosition
    slot: number
  }[]
}

export type HorsePollMode =
  | 'league'
  | 'three_in_four'
  | 'twins'
  | 'three_one'
  | 'more_chance'

export type HorseCourseStatus = 'upcoming' | 'finished'
export type HorsePollRoundStatus = 'open' | 'closed' | 'settled'

export interface Horse {
  id: number
  course_id: number
  name: string
  number: number
  photo_url?: string
  finish_position?: number | null
}

export interface HorseCourse {
  id: number
  name: string
  start_time: string
  status: HorseCourseStatus
  horses?: Horse[]
  created_at?: string
}

export interface HorsePollRoundCourse {
  id: number
  round_id: number
  course_id: number
  order_index: number
  course?: HorseCourse
}

export interface HorsePollLockedHorse {
  id: number
  round_id: number
  course_id: number
  horse_id: number
  horse?: Horse
}

export interface HorsePollRound {
  id: number
  mode: HorsePollMode
  title: string
  status: HorsePollRoundStatus
  month_key: string
  entry_fee_bronze: number
  entry_ads_required?: number
  entry_count?: number
  voting_open?: boolean
  courses?: HorsePollRoundCourse[]
  locked_horses?: HorsePollLockedHorse[]
}

export interface HorseCourseUsage {
  round_count: number
  entry_count: number
}

export type HorseEntryPayment = 'bronze' | 'ads'

export interface HorseEntryPaymentStatus {
  entry_fee_bronze: number
  entry_ads_required: number
  ads_watched: number
  can_pay_with_ads: boolean
  has_entry: boolean
}

export interface HorsePollEntry {
  id: number
  user_id: number
  round_id: number
  selections?: Record<string, number[]>
  paid_with?: string
  score: number
  correct_count: number
  is_winner: boolean
  prize_type?: string
  prize_amount?: number
  prize_awarded?: boolean
  created_at?: string
  user?: User
}

export interface HorseHistoryItem {
  entry: HorsePollEntry
  round?: HorsePollRound
}

export interface UserHorseLeagueScore {
  id?: number
  user_id: number
  month_key: string
  total_score: number
  daily_score: number
  user?: User
}
